/**
 * "Sites: Milton, London" beside the clinic selector, for a location-scoped
 * user (migration 0161).
 *
 * A scoped user sees a shorter Dispatch list, a shorter inbox, fewer locations
 * in every picker. Without this, that reads as data going missing rather than
 * as the narrowing an admin configured — and the first thing they do is file a
 * bug. Renders nothing at all for an unscoped user, which is everyone until an
 * admin sets a scope.
 *
 * Names come from the clinic's own location list, which the server has already
 * narrowed to the same set — so this is one cached query, not a second source
 * of truth. An id that resolves to no name renders verbatim (a site deleted
 * after it was granted), matching how the Dispatch column renders one.
 *
 * Plan: docs/plans/2026-09-20-user-location-scope.md
 */

import { MapPin } from 'lucide-react';
import { useLocationScope } from './use-location-scope';
import { useClinicLocations } from '../lib/useClinicLocations';

/** Beyond this, the chip names a count instead of a list. */
const MAX_NAMED = 3;

export function LocationScopeChip() {
  const { scoped, sites, unknown } = useLocationScope();
  const { locationName } = useClinicLocations();

  if (!scoped) return null;

  const names = sites.map((id) => locationName(id) || id);
  const sitesLabel =
    names.length === 0
      ? // Unknown-only: a triage account with no site of its own.
        'Unplaced only'
      : names.length <= MAX_NAMED
        ? names.join(', ')
        : `${names.length} sites`;
  const label = unknown && names.length > 0 ? `${sitesLabel} + unplaced` : sitesLabel;

  return (
    <span
      className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-lg"
      title={`Your account is limited to: ${label}`}
    >
      <MapPin size={12} className="shrink-0" />
      <span className="truncate max-w-[180px]">{label}</span>
    </span>
  );
}
